import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ShopService {
  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * Obtener todas las tiendas o filtrar por código
   * @param code - Código de tienda (opcional)
   * @param status - Estado de la tienda (opcional)
   * @returns Promise con la respuesta del servidor
   */
  getShops = async (code?: string, status?: boolean): Promise<any> => {
    try {
      let url = `${this.baseUrl}/api/shop`;
      const params: string[] = [];

      if (code) {
        params.push(`code=${code}`);
      }
      if (status !== undefined) {
        params.push(`status=${status}`);
      }

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      return lastValueFrom(
        this.httpClient.get<any>(url)
      );
    } catch (error) {
      console.error('Error fetching shops:', error);
      throw error;
    }
  };

  /**
   * Crear una nueva tienda
   * @param shopData - Datos de la tienda a crear
   * @returns Promise con la respuesta del servidor
   */
  createShop = async (shopData: any): Promise<any> => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/shop`, shopData, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('Error creating shop:', error);
      throw error;
    }
  };

  /**
   * Actualizar una tienda existente
   * @param code - Código de la tienda a actualizar
   * @param shopData - Datos a actualizar
   * @returns Promise con la respuesta del servidor
   */
  updateShop = async (code: string, shopData: any): Promise<any> => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/shop?code=${code}`, shopData, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('Error updating shop:', error);
      throw error;
    }
  };

  /**
   * Inactivar/Activar una tienda (toggle automático)
   * @param code - Código de la tienda
   * @returns Promise con la respuesta del servidor
   */
  toggleShopStatus = async (code: string): Promise<any> => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.patch<any>(`${this.baseUrl}/api/shop/toggle-status?code=${code}`, {}, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('Error toggling shop status:', error);
      throw error;
    }
  };

  /**
   * Actualizar el personal de una tienda
   * @param code - Código de la tienda
   * @param personal - Array de personal
   * @returns Promise con la respuesta del servidor
   */
  updateShopPersonal = async (code: string, personal: any[]): Promise<any> => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/shop/personal?code=${code}`, { personal }, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('Error updating shop personal:', error);
      throw error;
    }
  };

  /**
   * Eliminar un empleado del personal de una tienda
   * @param code - Código de la tienda
   * @param email - Email del empleado a eliminar
   * @returns Promise con la respuesta del servidor
   */
  deleteShopPersonal = async (code: string, email: string): Promise<any> => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.delete<any>(`${this.baseUrl}/api/shop/personal?code=${code}`, {
          headers: headers,
          body: { email }
        })
      );
    } catch (error) {
      console.error('Error deleting shop personal:', error);
      throw error;
    }
  };
}
